import Body from "app/components/Form/Body"

import InputField from "app/components/Form/Field/Input"
import SelectField from "app/components/Form/Field/Select"
import Heading from "app/components/Heading"
import Description from "app/components/Description"
import { useTranslate } from "hooks/translate"
import { useEffect } from "react"
import { CarModels } from "app/enums/carModels.enum"
import { RequestStatus } from "state/types"
import { useSelector } from "react-redux"
import {
  getVerifyLeadResponse,
  getLeadResponseStatus
} from "state/form/selectors"
import { FormStateData } from "state/form/types"
import { useFormContext } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { useAppflow } from "app/hooks/useAppFlow"
import { CarEnum } from "app/pages/appFlow/types"
import { validateAmount, validateMax, validateMin } from "app/validation"

// Same floor as the B2B leasing funnel, so both funnels accept the same range
// of used cars.
const MIN_CONSTRUCTION_YEAR = 2021

const carModels = [
  {
    key: "polestar_2",
    value: CarModels.POLESTAR_2
  },
  {
    key: "polestar_3",
    value: CarModels.POLESTAR_3
  },
  {
    key: "polestar_4",
    value: CarModels.POLESTAR_4
  },
  {
    key: "polestar_5",
    value: CarModels.POLESTAR_5
  }
]

const Step2: React.FC = () => {
  const { data: verified, status: verifyStatus } = useSelector(
    getVerifyLeadResponse
  )
  const { t } = useTranslate()
  const { clearErrors, setError } = useFormContext<FormStateData>()
  const { isRejected } = useSelector(getLeadResponseStatus)
  const { clientType, carType } = useAppflow()

  const carModelOptions = carModels
    .filter(
      model => carType === CarEnum.New || model.value !== CarModels.POLESTAR_5
    )
    .map(model => ({
      label: t(`system.car_model.${model.key}`),
      value: model.value
    }))

  useEffect(() => {
    if (verified) clearErrors()
  }, [verified])

  useEffect(() => {
    if (verifyStatus === RequestStatus.REJECTED) {
      setError("verificationCode", {
        message: t("error.verification_code_valid")
      })
    }
  }, [verifyStatus])

  if (isRejected) {
    return <Navigate to="/error" replace />
  }

  return (
    <>
      <Body>
        <Heading>{t(`form.step_3.${clientType}.heading`)}</Heading>
        <Description>{t(`form.step_3.${clientType}.body`)}</Description>
      </Body>
      <SelectField
        name="carModel"
        label="form.field.car_model.label"
        options={carModelOptions}
        rules={{ required: true }}
      />
      {/* KBC has a separate credit purpose code for used cars up to three
          years old, so we need the age of the car to pick the right one. */}
      {carType === CarEnum.Used && (
        <InputField
          name="constructionYear"
          inputMode="numeric"
          label="system.construction_year"
          rules={{
            required: true,
            validate: {
              amount: validateAmount,
              max: validateMax(new Date().getFullYear()),
              min: validateMin(MIN_CONSTRUCTION_YEAR)
            }
          }}
        />
      )}
    </>
  )
}

export default Step2
